import { useQuery } from '@tanstack/react-query';
import { invokeFunction, supabase } from '@/integrations/supabase/client';
import type { MerchantGmvFigures, MerchantGmvResponse } from '@/shared/merchantGmv';
import { readFunctionErrorBody } from '@/utils/functionErrorBody';

// The Merchants list's GMV figures (FIG-345 · UC-230), computed nightly and
// read through the admin-only merchant-gmv-v1.

export interface MerchantGmvData {
  byOrganisation: Map<string, MerchantGmvFigures>;
  latestAsOf: string | null;
}

export const merchantGmvQueryKey = ['merchant-gmv'] as const;

export async function fetchMerchantGmv(): Promise<MerchantGmvData> {
  let body: MerchantGmvResponse | null;
  try {
    const { data } = await invokeFunction<MerchantGmvResponse>(supabase, 'merchant-gmv-v1', { body: {} });
    body = data ?? null;
  } catch (error) {
    body = await readFunctionErrorBody<MerchantGmvResponse>(error);
    throw new Error(body?.error ?? (error instanceof Error ? error.message : 'Could not load GMV'));
  }
  if (!body?.success || !Array.isArray(body.merchants)) throw new Error(body?.error ?? 'Could not load GMV');
  return {
    byOrganisation: new Map(body.merchants.map((m) => [m.organisationId, m])),
    latestAsOf: body.latestAsOf ?? null,
  };
}

export function useMerchantGmv(enabled: boolean) {
  return useQuery({
    queryKey: merchantGmvQueryKey,
    queryFn: fetchMerchantGmv,
    enabled,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
}
